import React, { useEffect, useMemo, useState } from 'react';
import { Input, Pagination, Select, Typography } from '@attentive-platform/stem-ui';

import StateLozenge from './StateLozenge';
import useScopeRecords, { PAGE_SIZE_OPTIONS } from './useScopeRecords';
import { outcomeChipTone, typeChipTone } from './constants';
import { RecordsTabStyles as R, StatusChip, TableStyles as T } from './styles';

const COLUMNS = [
  { key: 'company', label: 'Company', sort: 'company' },
  { key: 'type', label: 'Type', sort: 'recordTypeLabel' },
  { key: 'trades', label: 'Trades scoped', sort: 'trades' },
  { key: 'version', label: 'Current version', sort: 'versionNum' },
  { key: 'state', label: 'State', sort: 'state' },
  { key: 'outcome', label: 'Outcome', sort: 'outcomeLabel' },
  { key: 'accountOwner', label: 'Account owner', sort: 'accountOwner' },
  { key: 'linked', label: 'Linked or adopted', sort: 'linkedOrAdopted' },
  { key: 'updated', label: 'Updated', sort: 'updatedAt' }
];

const RecordsTab = ({ onOpenRow, refreshToken }) => {
  const [query, setQuery] = useState('');
  const [expandedTrades, setExpandedTrades] = useState({});
  const [sort, setSort] = useState({ key: 'updatedAt', dir: 'desc' });
  const { rows, count, loading, refetch, page, setPage, pageSize, setPageSize, pageCount } = useScopeRecords({
    linked: true,
    query,
    stateFilter: 'All states',
    userFilter: 'All internal users',
    outcomeFilter: 'All outcomes',
    dateFilter: 'All time',
    sort
  });

  useEffect(() => {
    if (refreshToken !== undefined) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const rowsLabel = count === 1 ? '1 company record' : `${count} company records`;
  const pageSizeItems = useMemo(
    () => PAGE_SIZE_OPTIONS.map(n => ({ label: `${n} / page`, value: n })),
    []
  );

  const toggleTrades = (e, id) => {
    e.stopPropagation();
    setExpandedTrades(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const onSort = key => {
    setSort(prev =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  return (
    <T.Wrap>
      <T.Toolbar>
        <T.ToolbarLeft>
          <Typography variant='subtitle1-medium'>Company scope records</Typography>
          <Typography variant='body3' color='#666666'>
            Every record bound to a registered company — pilots once linked, existing customers from creation.
          </Typography>
        </T.ToolbarLeft>
        <Input
          size='large'
          placeholder='Search company or customer email'
          value={query}
          onChange={e => setQuery(e.target.value)}
          width='20rem'
        />
      </T.Toolbar>

      <T.TableBox>
        <R.HeaderGrid>
          {COLUMNS.map(c => (
            <T.SortHeader key={c.key} type='button' onClick={() => onSort(c.sort)}>
              {c.label}
              <T.SortMark>
                {sort.key === c.sort ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
              </T.SortMark>
            </T.SortHeader>
          ))}
        </R.HeaderGrid>
        {loading ? (
          <T.EmptySmall>Loading scope records…</T.EmptySmall>
        ) : rows.length === 0 ? (
          <T.EmptySmall>No linked scope records yet.</T.EmptySmall>
        ) : (
          rows.map(r => {
            const version = r.version?.display || '—';
            const Row =
              r.pilotStatus === 'closed' ||
              r.pilotStatus === 'closed_won' ||
              r.pilotStatus === 'closed_lost' ||
              r.outcome === 'live'
                ? R.RowClosed
                : R.RowOpen;
            const tradesOpen = !!expandedTrades[r.id];
            return (
              <Row key={r.id} onClick={() => onOpenRow && onOpenRow(r.id)}>
                <R.CompanyText>
                  <R.CompanyName>{r.linkedCompany || r.company}</R.CompanyName>
                  {r.adminEmail ? <R.AdminEmail>{r.adminEmail}</R.AdminEmail> : null}
                  {r.intakeName ? <R.ProspectLine>Intake name: {r.intakeName}</R.ProspectLine> : null}
                </R.CompanyText>
                <T.Cell>
                  <StatusChip tone={typeChipTone(r.isPilot)}>{r.recordTypeLabel}</StatusChip>
                </T.Cell>
                <T.Cell>
                  {r.trades > 0 ? (
                    <>
                      <R.TradesCount type='button' onClick={e => toggleTrades(e, r.id)}>
                        {r.trades}
                      </R.TradesCount>
                      {tradesOpen && <R.TradesExpanded>{r.tradeList.join(', ')}</R.TradesExpanded>}
                    </>
                  ) : (
                    <Typography variant='body3' color='#9E9E9E'>
                      —
                    </Typography>
                  )}
                </T.Cell>
                <T.CellStack>
                  <Typography variant='body3' sx={{ fontWeight: 500 }}>
                    {version}
                  </Typography>
                  {r.version?.date && (
                    <Typography variant='body5' color='#808080'>
                      {r.version.date}
                    </Typography>
                  )}
                </T.CellStack>
                <T.Cell>
                  <StateLozenge state={r.state} />
                </T.Cell>
                <T.Cell>
                  {r.outcomeLabel && r.outcomeLabel !== '—' ? (
                    <StatusChip
                      tone={outcomeChipTone({
                        isPilot: r.isPilot,
                        outcome: r.outcome,
                        pilotStatus: r.pilotStatus
                      })}>
                      {r.outcomeLabel}
                    </StatusChip>
                  ) : (
                    <Typography variant='body3' color='#9E9E9E'>
                      —
                    </Typography>
                  )}
                </T.Cell>
                <T.CellStack>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.accountOwner}
                  </Typography>
                  {r.accountOwnerRole ? (
                    <Typography variant='body5' color='#808080'>
                      {r.accountOwnerRole}
                    </Typography>
                  ) : null}
                </T.CellStack>
                <T.CellStack>
                  {r.linkedPrimary ? (
                    <>
                      <Typography variant='body3' color='#4D4D4D'>
                        {r.linkedPrimary}
                      </Typography>
                      {r.linkedAt ? (
                        <Typography variant='body5' color='#808080'>
                          {r.linkedAt}
                        </Typography>
                      ) : null}
                    </>
                  ) : (
                    <Typography variant='body3' color='#9E9E9E'>
                      —
                    </Typography>
                  )}
                </T.CellStack>
                <T.Cell>
                  <Typography variant='body3' color='#4D4D4D'>
                    {r.updated || r.created || '—'}
                  </Typography>
                </T.Cell>
              </Row>
            );
          })
        )}
      </T.TableBox>
      <T.FooterRow>
        <Typography variant='body3' color='#666666'>
          {rowsLabel}
        </Typography>
        <T.FooterRight>
          <T.SelectSmall>
            <Select
              size='small'
              variant='std-filled'
              items={pageSizeItems}
              value={pageSize}
              onChange={next => setPageSize(Number(next))}
            />
          </T.SelectSmall>
          {pageCount > 1 && (
            <Pagination
              size='small'
              color='primary'
              count={pageCount}
              page={page}
              onChange={(_, next) => setPage(next)}
            />
          )}
        </T.FooterRight>
      </T.FooterRow>
    </T.Wrap>
  );
};

export default RecordsTab;
